import React, { useEffect, useState } from 'react';
import { DeviceDetail, getDevice } from '../../api/device';
import { ApiError } from '../../api/request';
import { useDeviceCtx, useCan } from '../../store';
import DeviceTopSelector from './DeviceTopSelector';
import YamlImportModal from './YamlImportModal';

// 把后端返回的 Device + 子项拼成原型那种 YAML 视图字符串.
// 与原型 (doc/工艺参数平台-原型.html 的 "YAML 预览" tab) 同 schema:
//   - 顶层 version / profile_type / device 包
//   - 字段名走"长描述"风格 (standard_power_W / laser_source / mode_speed_config / ...)
//   - power_calibration 与 thermal_calibration 平铺在顶层
// 真实 YAML 原文需要从 MinIO 拉; 此处用 DB 字段反向重组等价 YAML.
function buildYaml(d: DeviceDetail): string {
    const archDisplay: Record<string, string> = {
        'laser-frame': '激光-框机',
        'laser-galvo': '激光-振镜',
        knife: '刀切',
        thermal: '热成型',
        embroidery: '刺绣',
        print: '打印',
    };

    // mode_speed_config 块, 把后端 modes 数组转成原型期望的 dict 形态.
    const modesByKey: Record<string, { isEnabled: boolean; speedMin?: number; speedMax?: number }> = {};
    for (const m of d.modes || []) modesByKey[m.mode] = m;
    const renderModeLine = (mode: string): string => {
        const m = modesByKey[mode];
        if (!m) return `    ${mode}: { enabled: false }`;
        const parts: string[] = [`enabled: ${m.isEnabled ? 'true' : 'false'}`];
        if (m.speedMin != null) parts.push(`speed_min_mm_s: ${m.speedMin}`);
        if (m.speedMax != null) parts.push(`speed_max_mm_s: ${m.speedMax}`);
        return `    ${mode}: { ${parts.join(', ')} }`;
    };

    // 能力字段来自 capability ext 组 (2026-08-14 store=column 机制移除)
    const cap = d.capability || {};

    // optics / timing / motion / supported_fixtures
    const opticsParts: string[] = [];
    if (cap.spotXMm != null) opticsParts.push(`spot_x_mm: ${cap.spotXMm}`);
    if (cap.spotYMm != null) opticsParts.push(`spot_y_mm: ${cap.spotYMm}`);
    const motionParts: string[] = [];
    if (cap.speedMinMmS != null) motionParts.push(`speed_min_mm_s: ${cap.speedMinMmS}`);
    if (cap.speedMaxMmS != null) motionParts.push(`speed_max_mm_s: ${cap.speedMaxMmS}`);
    if (cap.forbiddenSpeedBands && cap.forbiddenSpeedBands !== '[]' && cap.forbiddenSpeedBands !== 'null') {
        motionParts.push(`forbidden_speed_bands: ${cap.forbiddenSpeedBands}`);
    }

    // supported_fixtures: ext 组里是数组 (历史数据可能是 JSON 文本), 转成 yaml inline array.
    let fixturesLine = '';
    const fixtures = Array.isArray(cap.supportedFixtures)
        ? cap.supportedFixtures
        : (() => {
              try {
                  const arr = JSON.parse((cap.supportedFixtures as string) || '[]');
                  return Array.isArray(arr) ? arr.map(String) : [];
              } catch {
                  return [];
              }
          })();
    if (fixtures.length > 0) {
        fixturesLine = `  supported_fixtures: [${fixtures.map((x) => `"${x}"`).join(', ')}]`;
    }

    const lines: string[] = [];
    lines.push('version: 1');
    lines.push('profile_type: standalone_device');
    lines.push('device:');
    if (d.internalKey) lines.push(`  device_id: "${d.internalKey}"`);
    lines.push(`  device_key: "${d.deviceKey}"`);
    lines.push(`  device_family: "${d.family}"`);
    lines.push(`  device_architecture: "${archDisplay[d.archCode] || d.archCode}"`);
    if (cap.source) lines.push(`  laser_source: "${cap.source}"`);
    if (cap.band) lines.push(`  laser_band: "${cap.band}"`);
    if (cap.stdW != null) lines.push(`  standard_power_W: ${cap.stdW}`);
    if (cap.minStableW != null) lines.push(`  min_stable_power_W: ${cap.minStableW}`);
    if (cap.minStablePct != null) lines.push(`  min_stable_power_percent: ${cap.minStablePct}`);
    if (opticsParts.length > 0) lines.push(`  optics: { ${opticsParts.join(', ')} }`);
    if (cap.onDelayMs != null) lines.push(`  timing: { laser_on_delay_ms: ${cap.onDelayMs} }`);
    if (motionParts.length > 0) lines.push(`  motion: { ${motionParts.join(', ')} }`);
    lines.push('  mode_speed_config:');
    for (const mode of ['line', 'fill', 'bitmap', 'cut']) lines.push(renderModeLine(mode));
    if (fixturesLine) lines.push(fixturesLine);

    // power_calibration 在 device 块外, 顶层 inline map
    if (d.calibration && d.calibration.length > 0) {
        const sorted = [...d.calibration].sort((a, b) => a.pct - b.pct);
        const pairs = sorted.map((p) => `${p.pct}: ${p.realW}`).join(', ');
        lines.push('');
        lines.push(`power_calibration: { ${pairs} }`);
    }

    // thermal_calibration 顶层数组, 每项 inline 一行
    if (d.thermalCal && d.thermalCal.length > 0) {
        lines.push('');
        lines.push('thermal_calibration:');
        for (const t of d.thermalCal) {
            const parts: string[] = [`mode: ${t.mode}`, `material_type: ${t.materialType}`];
            if (t.tau != null) parts.push(`tau: ${t.tau}`);
            if (t.beta != null) parts.push(`beta: ${t.beta}`);
            lines.push(`  - { ${parts.join(', ')} }`);
        }
    }
    return lines.join('\n');
}

function DeviceYaml() {
    const canUpdate = useCan('device', 'update');
    const ctx = useDeviceCtx();
    const [detail, setDetail] = useState<DeviceDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showImport, setShowImport] = useState(false);

    useEffect(() => {
        if (!ctx.curDeviceKey) {
            setDetail(null);
            return;
        }
        setLoading(true);
        getDevice(ctx.curDeviceKey)
            .then((d) => {
                setDetail(d);
                setError('');
            })
            .catch((e) => {
                const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
                setError(msg);
                setDetail(null);
            })
            .finally(() => setLoading(false));
    }, [ctx.curDeviceKey]);

    const yaml = detail ? buildYaml(detail) : '';

    return (
        <div className="space-y-5">
            <div className="card p-4 flex items-center justify-between gap-3 flex-wrap">
                <DeviceTopSelector label="YAML 预览 · 当前设备" showCaps={false} />
                <button type="button" className="btn-ghost" onClick={() => setShowImport(true)} hidden={!canUpdate}>
                    ⬆ 导入 YAML
                </button>
            </div>

            <div className="text-xs text-slate-400">
                分层合并: family_defaults → shared_models →{' '}
                <span className="font-semibold text-slate-600">selected_device</span> →
                power_calibration
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    {error}
                </div>
            )}

            <div className="card overflow-hidden">
                <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <div className="text-xs text-slate-500">
                        {detail?.yamlRaw
                            ? '导入的 YAML 原文 (直接从 device.yaml_raw 读)'
                            : '(尚未导入 YAML 原文, 以下由后端字段反向重组)'}
                    </div>
                    {detail?.yamlHash && (
                        <span className="text-[10px] text-slate-400 font-mono">
                            sha256: {detail.yamlHash.slice(0, 12)}…
                        </span>
                    )}
                </div>
                <pre
                    className="p-4 text-[12px] font-mono overflow-auto text-slate-700"
                    style={{ minHeight: 360, lineHeight: 1.6 }}
                >
                    {loading ? '加载中…' : detail?.yamlRaw || yaml || '(无内容)'}
                </pre>
            </div>

            <div className="text-[11px] text-slate-400">
                · 此页基于 DB 字段重组生成可读 YAML, 用于核对设备工况.
            </div>

            <YamlImportModal
                open={showImport}
                onClose={() => setShowImport(false)}
                onImported={() => {
                    if (ctx.curDeviceKey) {
                        getDevice(ctx.curDeviceKey).then(setDetail).catch(() => {});
                    }
                }}
            />
        </div>
    );
}

export default DeviceYaml;
